// Phase 1.6 (hh-9x7) — AIClarify bubble.
// Inline clarification: AI asks "which one?" and shows N option chips.
// On tap, the parent re-POSTs the chosen text back as a user message; this
// component only emits onPick(option). Acceptance (case-28): the clarification
// is the NEXT message in the feed, not a modal.
//
// Ported from designproject/files/screens-feed.jsx (AIClarify). Diffs:
//   • options are AIClarifyOption objects with id/label/hint, not bare strings
//   • when resolvedOptionId is set, options render disabled with a check on
//     the chosen one (source has no resolved state — it was UI-only there).

import type { JSX } from 'preact';
import { T, Icon, Avatar } from '../../tokens';
import type { AIClarifyMessage, AIClarifyOption } from './Message';

export interface AIClarifyProps {
  readonly msg: AIClarifyMessage;
  readonly onPick?: (option: AIClarifyOption) => void;
}

interface OptionChipProps {
  readonly option: AIClarifyOption;
  readonly resolved: boolean;
  readonly chosen: boolean;
  readonly onPick?: (option: AIClarifyOption) => void;
}

function OptionChip({ option, resolved, chosen, onPick }: OptionChipProps): JSX.Element {
  const handleClick = !resolved && onPick !== undefined ? () => onPick(option) : undefined;
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={resolved}
      style={{
        display: 'inline-flex',
        flexDirection: option.hint !== undefined ? 'column' : 'row',
        alignItems: option.hint !== undefined ? 'flex-start' : 'center',
        gap: option.hint !== undefined ? 2 : 6,
        background: chosen ? T.brandSoft : T.bg,
        border: `1px solid ${chosen ? T.brandSoft2 : T.border}`,
        color: chosen ? T.brandDeep : T.ink,
        fontFamily: T.font,
        fontSize: 13,
        fontWeight: 600,
        padding: option.hint !== undefined ? '8px 14px' : '8px 14px',
        borderRadius: option.hint !== undefined ? 14 : 999,
        cursor: resolved ? 'default' : 'pointer',
        opacity: resolved && !chosen ? 0.55 : 1,
        textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {chosen && <Icon name="check" size={13} color={T.brandDeep} />}
        <span>{option.label}</span>
      </span>
      {option.hint !== undefined && (
        <span style={{ fontSize: 11.5, color: T.ink3, fontWeight: 500 }}>{option.hint}</span>
      )}
    </button>
  );
}

export function AIClarify({ msg, onPick }: AIClarifyProps): JSX.Element {
  const resolved = msg.resolvedOptionId !== undefined;
  return (
    <div style={{ padding: '4px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <Avatar kind="ai" icon="info" size={28} />
      <div style={{ minWidth: 0, maxWidth: '78%' }}>
        <div
          style={{
            background: T.surface,
            border: `1px dashed ${T.borderStrong}`,
            padding: '12px 14px',
            borderRadius: '4px 18px 18px 18px',
            fontSize: 14.5,
            lineHeight: 1.4,
            color: T.ink,
            fontWeight: 500,
          }}
        >
          <div style={{ marginBottom: 10 }}>{msg.text}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {msg.options.map((o) => {
              const chosen = resolved && msg.resolvedOptionId === o.id;
              const chipProps: OptionChipProps =
                onPick !== undefined
                  ? { option: o, resolved, chosen, onPick }
                  : { option: o, resolved, chosen };
              return <OptionChip key={o.id} {...chipProps} />;
            })}
          </div>
          {!resolved && (
            <div style={{ marginTop: 8, fontSize: 11.5, color: T.ink3, fontWeight: 500 }}>
              Запись не сохранилась — выбери вариант, и я закончу.
            </div>
          )}
        </div>
        {msg.time !== undefined && (
          <div style={{ fontSize: 11, color: T.ink3, marginTop: 4, fontWeight: 500 }}>
            {msg.time}
          </div>
        )}
      </div>
    </div>
  );
}
